import { ref, computed } from 'vue'
import pricingConfig from '@/config/pricing.config.json'

// Mapping des pays vers les zones/devises (conservé pour la détection)
export const COUNTRY_TO_ZONE = {
  // Afrique de l'Ouest - Zone XOF
  'BJ': 'xof', // Bénin
  'BF': 'xof', // Burkina Faso
  'CI': 'xof', // Côte d'Ivoire
  'SN': 'xof', // Sénégal
  'ML': 'xof', // Mali
  'NE': 'xof', // Niger
  'TG': 'xof', // Togo
  'GW': 'xof', // Guinée-Bissau

  // Europe - Zone EUR
  'IT': 'eur', // Italie
  'FR': 'eur', // France
  'BE': 'eur', // Belgique
  'DE': 'eur', // Allemagne
  'ES': 'eur', // Espagne
  'PT': 'eur', // Portugal
  'NL': 'eur', // Pays-Bas
  'CH': 'eur', // Suisse
  'AT': 'eur', // Autriche
  'GB': 'eur', // Royaume-Uni
  'LU': 'eur', // Luxembourg
  'IE': 'eur', // Irlande
  'GR': 'eur', // Grèce
  'SE': 'eur', // Suède
  'NO': 'eur', // Norvège
  'DK': 'eur', // Danemark
  'FI': 'eur', // Finlande
  'PL': 'eur', // Pologne
  'CZ': 'eur', // République tchèque
  'HU': 'eur', // Hongrie
  'RO': 'eur', // Roumanie
  'BG': 'eur', // Bulgarie
  'HR': 'eur', // Croatie
  'SI': 'eur', // Slovénie
  'SK': 'eur', // Slovaquie
  'LT': 'eur', // Lituanie
  'LV': 'eur', // Lettonie
  'EE': 'eur', // Estonie

  // Amérique du Nord - Zone CAD/USD
  'CA': 'cad', // Canada
  'US': 'usd', // États-Unis

  // Autres pays africains - Zone XOF
  'CM': 'xof', // Cameroun
  'CD': 'xof', // RD Congo
  'CG': 'xof', // Congo
  'GA': 'xof', // Gabon
  'MG': 'xof', // Madagascar
  'MA': 'xof', // Maroc
  'TN': 'xof', // Tunisie
  'DZ': 'xof', // Algérie
  'KE': 'xof', // Kenya
  'UG': 'xof', // Ouganda
  'TZ': 'xof', // Tanzanie
  'RW': 'xof', // Rwanda
  'BI': 'xof', // Burundi
  'ET': 'xof', // Éthiopie
  'GH': 'xof', // Ghana
  'NG': 'xof', // Nigeria
  'ZA': 'xof', // Afrique du Sud

  // Asie - Zone EUR
  'CN': 'eur', // Chine
  'JP': 'eur', // Japon
  'IN': 'eur', // Inde
  'SG': 'eur', // Singapour
  'MY': 'eur', // Malaisie
  'TH': 'eur', // Thaïlande
  'VN': 'eur', // Vietnam
  'PH': 'eur', // Philippines
  'ID': 'eur', // Indonésie
  'KR': 'eur', // Corée du Sud
  'AE': 'eur', // Émirats arabes unis
  'SA': 'eur', // Arabie saoudite
  'QA': 'eur', // Qatar
  'IL': 'eur', // Israël
  'LB': 'eur', // Liban
  'TR': 'eur', // Turquie

  // Amérique du Sud/Centrale - Zone CAD
  'BR': 'cad', // Brésil
  'AR': 'cad', // Argentine
  'MX': 'cad', // Mexique
  'CO': 'cad', // Colombie
  'PE': 'cad', // Pérou
  'CL': 'cad', // Chili
  'VE': 'cad', // Venezuela
  'EC': 'cad', // Équateur

  // Océanie - Zone CAD
  'AU': 'cad', // Australie
  'NZ': 'cad', // Nouvelle-Zélande
  'FJ': 'cad', // Fidji
  'PG': 'cad'  // Papouasie-Nouvelle-Guinée
}

// Mapping des continents vers les devises (fallback)
export const CONTINENT_TO_ZONE = {
  'Africa': 'xof',
  'Europe': 'eur',
  'Asia': 'eur',
  'North America': 'cad',
  'South America': 'cad',
  'Oceania': 'cad'
}

// Détection de la zone via IP
export const detectUserZone = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/')
    const data = await response.json()

    console.log('🌍 IP Detection Result:', {
      country: data.country_code,
      countryName: data.country_name,
      continent: data.continent,
      ip: data.ip
    })

    // Essayer d'abord par code pays
    if (data.country_code && COUNTRY_TO_ZONE[data.country_code]) {
      const zone = COUNTRY_TO_ZONE[data.country_code]
      console.log(`✅ Country detected: ${data.country_code} (${data.country_name}) -> ${zone}`)
      return zone
    }

    // Fallback par continent
    if (data.continent && CONTINENT_TO_ZONE[data.continent]) {
      const zone = CONTINENT_TO_ZONE[data.continent]
      console.log(`✅ Continent detected: ${data.continent} -> ${zone}`)
      return zone
    }

    console.log(`⚠️ No specific zone found, using default (xof)`)
  } catch (error) {
    console.warn('❌ IP detection failed:', error.message)
  }

  console.log('⚠️ Defaulting to xof (Africa)')
  return 'xof'
}

// Formatage des prix
export const formatPrice = (amount, currency, locale) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: currency === 'XOF' ? 0 : 2
  })

  return formatter.format(amount)
}

// Singleton pour partager l'état entre tous les composants
let pricingInstance = null

function createPricingInstance() {
  const userZone = ref('xof')
  const isLoading = ref(true)

  // Initialiser la détection de zone
  const initZoneDetection = async () => {
    isLoading.value = true
    console.log('🔍 Starting zone detection...')

    try {
      const detectedZone = await detectUserZone()
      userZone.value = detectedZone
      console.log(`✅ Final zone: ${detectedZone}`)
    } catch (error) {
      console.warn('❌ Zone detection failed:', error)
      userZone.value = 'xof'
    } finally {
      isLoading.value = false
    }
  }

  // Obtenir la config de zone actuelle
  const currentZoneConfig = computed(() => {
    return pricingConfig.zones[userZone.value] || pricingConfig.zones.xof
  })

  const getProductInfo = (productId) => {
    const product = pricingConfig.products[productId]
    if (!product) return null

    const zoneInfo = currentZoneConfig.value
    const amount = product.prices[userZone.value]
    const providers = product.providers[userZone.value] || []

    let formattedPrice = formatPrice(amount, zoneInfo.currency, zoneInfo.locale)
    
    // Override de l'affichage si le produit est gratuit ou sur devis
    if (providers.includes('none')) {
      formattedPrice = 'Gratuit'
    } else if (providers.includes('quote')) {
      formattedPrice = 'Sur Devis'
    }

    return {
      ...product,
      amount,
      currency: zoneInfo.currency,
      symbol: zoneInfo.symbol,
      locale: zoneInfo.locale,
      formattedPrice,
      zoneName: zoneInfo.name,
      zoneFlag: zoneInfo.flag
    }
  }

  // --- Rétro-compatibilité ---

  // Configuration de prix actuelle pour les certifications (fallback sur cert_soft_skills_standard)
  const currentCertificationPricing = computed(() => {
    return getProductInfo('cert_soft_skills_standard')
  })

  const certificationPrice = computed(() => currentCertificationPricing.value.formattedPrice)
  const certificationPriceRaw = computed(() => currentCertificationPricing.value.amount)
  
  // Configuration de prix actuelle pour les dons
  const currentDonationPricing = computed(() => {
    return getProductInfo('donation')
  })

  const donationPrice = computed(() => currentDonationPricing.value.formattedPrice)
  const donationPriceRaw = computed(() => currentDonationPricing.value.amount)

  // Infos de zone
  const currencySymbol = computed(() => currentZoneConfig.value.symbol)
  const currencyCode = computed(() => currentZoneConfig.value.currency)
  const currencyFlag = computed(() => currentZoneConfig.value.flag)
  const zoneDisplayName = computed(() => currentZoneConfig.value.name)

  return {
    userZone,
    currencyCode,
    currencySymbol,
    currencyFlag,
    zoneDisplayName,
    isLoading,
    certificationPrice,
    certificationPriceRaw,
    donationPrice,
    donationPriceRaw,
    initZoneDetection,
    formatPrice,
    getProductInfo,
    pricingConfig
  }
}

// Composable principal qui retourne toujours la même instance
export function usePricing() {
  if (!pricingInstance) {
    pricingInstance = createPricingInstance()
  }
  return pricingInstance
}
